import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, useDragControls, useMotionValue } from 'motion/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import * as cadviewer from 'cadviewer';
import jQuery from 'jquery';
import { useSpaceToolsStore } from '../SpaceTools/stores/spaceToolsStore';

interface TextManipulationPanelProps {
  isOpen: boolean;
}

interface FontOption {
  name: string;
  family: string;
  category: string;
}

interface TextPreset {
  name: string;
  font: FontOption;
  fontSize: number;
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
  color?: string;
}

// Font size presets
const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 36, 48, 72];

// localStorage key for custom presets
const PRESETS_STORAGE_KEY = 'textManipulation_customPresets';

// Font categories for organization
const FONT_CATEGORIES = [
  'Sans-Serif',
  'Serif',
  'Monospace',
  'Korean',
  'Japanese',
  'Chinese (Simplified)',
  'Chinese (Traditional)',
  'Arabic',
  'Hebrew',
  'Thai'
];

// Comprehensive font list with multilingual support
const FONTS: FontOption[] = [
  // Sans-Serif (Western)
  { name: 'Arial', family: 'Arial, sans-serif', category: 'Sans-Serif' },
  { name: 'Helvetica', family: 'Helvetica, Arial, sans-serif', category: 'Sans-Serif' },
  { name: 'Verdana', family: 'Verdana, sans-serif', category: 'Sans-Serif' },
  { name: 'Tahoma', family: 'Tahoma, Geneva, sans-serif', category: 'Sans-Serif' },
  { name: 'Trebuchet MS', family: '"Trebuchet MS", sans-serif', category: 'Sans-Serif' },
  { name: 'Geneva', family: 'Geneva, Tahoma, sans-serif', category: 'Sans-Serif' },
  { name: 'Lucida Grande', family: '"Lucida Grande", "Lucida Sans", sans-serif', category: 'Sans-Serif' },

  // Serif (Western)
  { name: 'Times New Roman', family: '"Times New Roman", Times, serif', category: 'Serif' },
  { name: 'Times', family: 'Times, "Times New Roman", serif', category: 'Serif' },
  { name: 'Georgia', family: 'Georgia, serif', category: 'Serif' },
  { name: 'Palatino', family: 'Palatino, "Palatino Linotype", serif', category: 'Serif' },
  { name: 'Garamond', family: 'Garamond, serif', category: 'Serif' },
  { name: 'Book Antiqua', family: '"Book Antiqua", Palatino, serif', category: 'Serif' },
  { name: 'Baskerville', family: 'Baskerville, "Times New Roman", serif', category: 'Serif' },

  // Monospace
  { name: 'Courier New', family: '"Courier New", Courier, monospace', category: 'Monospace' },
  { name: 'Courier', family: 'Courier, "Courier New", monospace', category: 'Monospace' },
  { name: 'Monaco', family: 'Monaco, Consolas, monospace', category: 'Monospace' },
  { name: 'Consolas', family: 'Consolas, Monaco, monospace', category: 'Monospace' },

  // Korean
  { name: 'Malgun Gothic', family: '"Malgun Gothic", "Apple SD Gothic Neo", AppleGothic, sans-serif', category: 'Korean' },
  { name: 'Batang', family: 'Batang, serif', category: 'Korean' },
  { name: 'Dotum', family: 'Dotum, sans-serif', category: 'Korean' },
  { name: 'Gulim', family: 'Gulim, sans-serif', category: 'Korean' },
  { name: 'AppleGothic', family: 'AppleGothic, "Apple SD Gothic Neo", sans-serif', category: 'Korean' },
  { name: 'Apple SD Gothic Neo', family: '"Apple SD Gothic Neo", AppleGothic, sans-serif', category: 'Korean' },

  // Japanese
  { name: 'MS Gothic', family: '"MS Gothic", Meiryo, "Hiragino Sans", sans-serif', category: 'Japanese' },
  { name: 'MS Mincho', family: '"MS Mincho", serif', category: 'Japanese' },
  { name: 'MS PGothic', family: '"MS PGothic", sans-serif', category: 'Japanese' },
  { name: 'Meiryo', family: 'Meiryo, "Hiragino Sans", "MS Gothic", sans-serif', category: 'Japanese' },
  { name: 'Hiragino Sans', family: '"Hiragino Sans", "Hiragino Kaku Gothic ProN", Meiryo, sans-serif', category: 'Japanese' },
  { name: 'Hiragino Mincho ProN', family: '"Hiragino Mincho ProN", serif', category: 'Japanese' },

  // Chinese (Simplified)
  { name: 'SimSun', family: 'SimSun, serif', category: 'Chinese (Simplified)' },
  { name: 'SimHei', family: 'SimHei, sans-serif', category: 'Chinese (Simplified)' },
  { name: 'Microsoft YaHei', family: '"Microsoft YaHei", "PingFang SC", SimHei, sans-serif', category: 'Chinese (Simplified)' },
  { name: 'PingFang SC', family: '"PingFang SC", "Microsoft YaHei", sans-serif', category: 'Chinese (Simplified)' },
  { name: 'STHeiti', family: 'STHeiti, sans-serif', category: 'Chinese (Simplified)' },

  // Chinese (Traditional)
  { name: 'MingLiU', family: 'MingLiU, serif', category: 'Chinese (Traditional)' },
  { name: 'Microsoft JhengHei', family: '"Microsoft JhengHei", "PingFang TC", MingLiU, sans-serif', category: 'Chinese (Traditional)' },
  { name: 'PingFang TC', family: '"PingFang TC", "Microsoft JhengHei", sans-serif', category: 'Chinese (Traditional)' },

  // Arabic
  { name: 'Tahoma (Arabic)', family: 'Tahoma, "Geeza Pro", "Arial Arabic", sans-serif', category: 'Arabic' },
  { name: 'Geeza Pro', family: '"Geeza Pro", Tahoma, sans-serif', category: 'Arabic' },
  { name: 'Arial Arabic', family: '"Arial Arabic", Tahoma, sans-serif', category: 'Arabic' },

  // Hebrew
  { name: 'Arial (Hebrew)', family: 'Arial, "Arial Hebrew", David, sans-serif', category: 'Hebrew' },
  { name: 'Arial Hebrew', family: '"Arial Hebrew", Arial, sans-serif', category: 'Hebrew' },
  { name: 'David', family: 'David, serif', category: 'Hebrew' },

  // Thai
  { name: 'Tahoma (Thai)', family: 'Tahoma, Thonburi, "Cordia New", sans-serif', category: 'Thai' },
  { name: 'Thonburi', family: 'Thonburi, sans-serif', category: 'Thai' },
  { name: 'Cordia New', family: '"Cordia New", sans-serif', category: 'Thai' },
];

const TextManipulationPanel: React.FC<TextManipulationPanelProps> = ({ isOpen }) => {
  const { closePanel } = useSpaceToolsStore();

  // State
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [selectedFont, setSelectedFont] = useState<FontOption>(FONTS[0]);
  const [currentFontSize, setCurrentFontSize] = useState<number>(12);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isFontDropdownOpen, setIsFontDropdownOpen] = useState(false);
  const [isSizeDropdownOpen, setIsSizeDropdownOpen] = useState(false);

  // Custom presets state
  const [customPresets, setCustomPresets] = useState<TextPreset[]>([]);
  const [showSavePresetDialog, setShowSavePresetDialog] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');

  // Drag and resize state
  const [modalWidth] = useState(380);
  const [modalHeight] = useState(420);
  const modalRef = useRef<HTMLDivElement>(null);

  // Motion values for position
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  // Drag controls
  const dragControls = useDragControls();

  // Constants for font manipulation (from original file)
  const fontfactor = 1.1;

  // Drag constraints
  const dragConstraints = useMemo(
    () => ({
      left: -(window.innerWidth - 100),
      right: window.innerWidth - 100,
      top: -(window.innerHeight - 100),
      bottom: window.innerHeight - 100,
    }),
    []
  );

  // Reset style states to default
  const resetStyleStates = () => {
    setIsBold(false);
    setIsItalic(false);
    setIsUnderline(false);
    setCurrentFontSize(12);
    setSelectedFont(FONTS[0]);
  };

  // Load custom presets from localStorage
  const loadPresetsFromStorage = () => {
    try {
      const stored = localStorage.getItem(PRESETS_STORAGE_KEY);
      if (stored) {
        const presets = JSON.parse(stored) as TextPreset[];
        setCustomPresets(presets);
      }
    } catch (error) {
      console.error('Error loading presets from localStorage:', error);
    }
  };

  // Save current settings as a new preset
  const savePreset = () => {
    if (!newPresetName.trim()) {
      alert('Please enter a preset name');
      return;
    }

    const textElementID = cadviewer.cvjs_returnFoundTextElement();
    let color = '#000000';
    if (textElementID) {
      color = jQuery('#' + textElementID).css('color') || '#000000';
    }

    const newPreset: TextPreset = {
      name: newPresetName.trim(),
      font: selectedFont,
      fontSize: currentFontSize,
      isBold,
      isItalic,
      isUnderline,
      color,
    };

    const updatedPresets = [...customPresets, newPreset];
    setCustomPresets(updatedPresets);

    try {
      localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(updatedPresets));
      setShowSavePresetDialog(false);
      setNewPresetName('');
      console.log('Preset saved:', newPreset.name);
    } catch (error) {
      console.error('Error saving preset to localStorage:', error);
      alert('Failed to save preset');
    }
  };

  // Load a preset and apply it to selected element
  const loadPreset = (preset: TextPreset) => {
    const textElementID = cadviewer.cvjs_returnFoundTextElement();
    if (!textElementID) {
      alert('Please select a text element first');
      return;
    }

    // Apply styles
    jQuery('#' + textElementID).css({
      'font-family': preset.font.family,
      'font-size': preset.fontSize + 'px',
      'font-weight': preset.isBold ? 'bold' : 'normal',
      'font-style': preset.isItalic ? 'italic' : 'normal',
      'text-decoration': preset.isUnderline ? 'underline' : 'none',
      'color': preset.color || '#000000',
    });

    // Update state
    setSelectedFont(preset.font);
    setCurrentFontSize(preset.fontSize);
    setIsBold(preset.isBold);
    setIsItalic(preset.isItalic);
    setIsUnderline(preset.isUnderline);

    console.log('Preset loaded:', preset.name);
  };

  // Delete a custom preset
  const deletePreset = (presetName: string) => {
    if (!window.confirm(`Delete preset "${presetName}"?`)) {
      return;
    }

    const updatedPresets = customPresets.filter(p => p.name !== presetName);
    setCustomPresets(updatedPresets);

    try {
      localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(updatedPresets));
      console.log('Preset deleted:', presetName);
    } catch (error) {
      console.error('Error deleting preset from localStorage:', error);
    }
  };

  // Update UI state from selected element's current styles
  const updateStylesFromElement = (elementId: string) => {
    const element = jQuery('#' + elementId);

    // Read current styles with null/undefined safety
    const fontWeight = element.css('font-weight') || 'normal';
    const fontStyle = element.css('font-style') || 'normal';
    const textDecoration = element.css('text-decoration') || 'none';
    const fontSizeStr = element.css('font-size') || '12px';
    const fontSize = parseFloat(fontSizeStr);
    const fontFamily = element.css('font-family') || '';

    // Update state with safe checks
    setIsBold(fontWeight === 'bold' || parseInt(fontWeight) >= 600);
    setIsItalic(fontStyle === 'italic');
    setIsUnderline(textDecoration.includes('underline'));
    setCurrentFontSize(Math.round(fontSize) || 12);

    // Find matching font in FONTS array
    if (fontFamily) {
      const matchingFont = FONTS.find(f => fontFamily.toLowerCase().includes(f.name.toLowerCase()));
      if (matchingFont) setSelectedFont(matchingFont);
    }
  };

  // Reset position when modal opens
  useEffect(() => {
    if (isOpen) {
      x.set(0);
      y.set(0);
      setSelectedElementId(null);
      resetStyleStates();
    }
  }, [isOpen, x, y]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.font-dropdown') && !target.closest('.size-dropdown')) {
        setIsFontDropdownOpen(false);
        setIsSizeDropdownOpen(false);
      }
    };

    if (isFontDropdownOpen || isSizeDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isFontDropdownOpen, isSizeDropdownOpen]);

  // Load custom presets from localStorage on mount
  useEffect(() => {
    loadPresetsFromStorage();
  }, []);

  // Handle text element selection - Using exact method from original file
  const handleSelectTextElement = () => {
    cadviewer.cvjs_findTextElement(true);

    // Wait a bit for the selection to complete, then get the element
    const checkInterval = setInterval(() => {
      const elementId = cadviewer.cvjs_returnFoundTextElement();
      if (elementId) {
        setSelectedElementId(elementId);
        updateStylesFromElement(elementId); // Read and sync element styles
        clearInterval(checkInterval); // Stop checking once found
        console.log('Selected text element:', elementId);
      }
    }, 1000);

    // Clear interval after 10 seconds to prevent memory leak
    setTimeout(() => clearInterval(checkInterval), 10000);
  };

  // Increase Font - Jump to next preset size or multiply by factor
  const handleIncreaseFonts = () => {
    const textElementID = cadviewer.cvjs_returnFoundTextElement();

    if (!textElementID) {
      alert('Please select a text element first');
      return;
    }

    const currentIndex = FONT_SIZES.findIndex(size => size >= currentFontSize);
    let newSize: number;

    if (currentIndex === -1 || currentIndex === FONT_SIZES.length - 1) {
      // If larger than all presets or at max, multiply by factor
      newSize = Math.round(currentFontSize * fontfactor);
    } else {
      // Jump to next preset
      newSize = FONT_SIZES[currentIndex + 1];
    }

    setCurrentFontSize(newSize);
    jQuery('#' + textElementID).css('font-size', newSize + 'px');
    console.log('Increased font to:', newSize);
  };

  // Decrease Font - Jump to previous preset size or divide by factor
  const handleDecreaseFonts = () => {
    const textElementID = cadviewer.cvjs_returnFoundTextElement();

    if (!textElementID) {
      alert('Please select a text element first');
      return;
    }

    const currentIndex = FONT_SIZES.findIndex(size => size >= currentFontSize);
    let newSize: number;

    if (currentIndex <= 0) {
      // If smaller than all presets or at min, divide by factor
      newSize = Math.max(1, Math.round(currentFontSize / fontfactor));
    } else {
      // Jump to previous preset
      newSize = FONT_SIZES[currentIndex - 1];
    }

    setCurrentFontSize(newSize);
    jQuery('#' + textElementID).css('font-size', newSize + 'px');
    console.log('Decreased font to:', newSize);
  };

  // Handle font family change
  const handleFontChange = (font: FontOption) => {
    const textElementID = cadviewer.cvjs_returnFoundTextElement();
    if (!textElementID) {
      alert('Please select a text element first');
      return;
    }
    jQuery('#' + textElementID).css('font-family', font.family);
    setSelectedFont(font);
    setIsFontDropdownOpen(false);
  };

  // Handle font size change from dropdown
  const handleFontSizeChange = (size: number) => {
    const textElementID = cadviewer.cvjs_returnFoundTextElement();
    if (!textElementID) {
      alert('Please select a text element first');
      return;
    }
    jQuery('#' + textElementID).css('font-size', size + 'px');
    setCurrentFontSize(size);
    setIsSizeDropdownOpen(false);
  };

  // Toggle Bold
  const handleToggleBold = () => {
    const textElementID = cadviewer.cvjs_returnFoundTextElement();
    if (!textElementID) {
      alert('Please select a text element first');
      return;
    }

    const newBoldState = !isBold;
    setIsBold(newBoldState);

    jQuery('#' + textElementID).css({
      'font-weight': newBoldState ? 'bold' : 'normal'
    });
  };

  // Toggle Italic
  const handleToggleItalic = () => {
    const textElementID = cadviewer.cvjs_returnFoundTextElement();
    if (!textElementID) {
      alert('Please select a text element first');
      return;
    }

    const newItalicState = !isItalic;
    setIsItalic(newItalicState);

    jQuery('#' + textElementID).css({
      'font-style': newItalicState ? 'italic' : 'normal'
    });
  };

  // Toggle Underline
  const handleToggleUnderline = () => {
    const textElementID = cadviewer.cvjs_returnFoundTextElement();
    if (!textElementID) {
      alert('Please select a text element first');
      return;
    }

    const newUnderlineState = !isUnderline;
    setIsUnderline(newUnderlineState);

    jQuery('#' + textElementID).css({
      'text-decoration': newUnderlineState ? 'underline' : 'none'
    });
  };

  // Normal Style - Using exact method from original file
  const handleNormalStyle = () => {
    const textElementID = cadviewer.cvjs_returnFoundTextElement();

    if (!textElementID) {
      alert('Please select a text element first');
      return;
    }

    jQuery('#' + textElementID).css({
      'font-family': 'Arial, helvetica, sans-serif',
      'font-style': 'normal',
      'font-weight': 'normal',
      'text-decoration': 'none',
      'color': '#F33',
      'text-anchor': 'start',
      'dominant-baseline': 'auto'
    });

    // Update state to reflect applied styles
    setSelectedFont(FONTS.find(f => f.name === 'Arial') || FONTS[0]);
    setIsBold(false);
    setIsItalic(false);
    setIsUnderline(false);

    console.log('Applied normal style');
  };

  // Italic Style - Using exact method from original file
  const handleItalicStyle = () => {
    const textElementID = cadviewer.cvjs_returnFoundTextElement();

    if (!textElementID) {
      alert('Please select a text element first');
      return;
    }

    jQuery('#' + textElementID).css({
      'font-family': 'Georgia, serif',
      'font-style': 'italic',
      'font-weight': 'bold',
      'text-decoration': 'none',
      'color': '#33F',
      'text-anchor': 'middle',
      'dominant-baseline': 'auto'
    });

    // Update state to reflect applied styles
    setSelectedFont(FONTS.find(f => f.name === 'Georgia') || FONTS[0]);
    setIsBold(true);
    setIsItalic(true);
    setIsUnderline(false);

    console.log('Applied italic style');
  };

  // Handle close
  const handleClose = () => {
    closePanel();
    setSelectedElementId(null);
    resetStyleStates();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[1000000004] w-full h-full" style={{ pointerEvents: 'none' }}>
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" style={{ pointerEvents: 'none' }}>
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        <motion.div
          ref={modalRef}
          drag
          dragListener={false}
          dragControls={dragControls}
          dragConstraints={dragConstraints}
          dragElastic={0}
          dragMomentum={false}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            marginLeft: -(modalWidth / 2),
            marginTop: -(modalHeight / 2),
            x,
            y,
            width: modalWidth,
            height: modalHeight,
            maxWidth: window.innerWidth - 40,
            maxHeight: window.innerHeight - 40,
            pointerEvents: 'auto',
          }}
          className="bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 relative flex flex-col"
        >
          {/* Header - Draggable */}
          <div
            className="flex-none px-4 py-2.5 border-b cursor-move select-none"
            style={{
              background: '#0f3557',
              borderBottomColor: '#0a2540',
              userSelect: 'none',
            }}
            onPointerDown={(e) => dragControls.start(e)}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg leading-6 font-bold text-white flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-5 h-5"
                >
                  <polyline points="4 7 4 4 20 4 20 7" />
                  <line x1="9" y1="20" x2="15" y2="20" />
                  <line x1="12" y1="4" x2="12" y2="20" />
                </svg>
                Text Manipulation
              </h3>
              <button
                onClick={handleClose}
                className="text-white transition-colors"
                style={{ opacity: 0.9 }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.7';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '0.9';
                }}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-0.5 text-xs" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              Select and modify text properties
            </p>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-auto p-4" style={{ minHeight: 0 }}>
            {/* Step 1: Select Text Element */}
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#0f3557] text-white text-xs font-bold">
                  1
                </span>
                Select Text Element
              </h4>
              <button
                onClick={handleSelectTextElement}
                className="w-full px-3 py-2 bg-[#0f3557] text-white font-medium rounded-md hover:bg-[#0a2540] transition-all shadow-sm flex items-center justify-center gap-2 text-sm"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 11a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" />
                  <path d="M12 2a9 9 0 1 0 9 9" />
                  <path d="M18 5l1 1" />
                  <path d="M15 2l1 1" />
                  <path d="M21 8l1 1" />
                </svg>
                Click to Select Text on Canvas
              </button>
              {selectedElementId && (
                <p className="mt-1.5 text-xs text-green-600 font-medium">
                  ✓ Element selected: {selectedElementId}
                </p>
              )}
            </div>

            {/* Divider */}
            <div className="h-px bg-gray-200 my-4" />

            {/* Step 2: Modify Text Properties */}
            <div className={`space-y-3 ${!selectedElementId ? 'opacity-50 pointer-events-none' : ''}`}>
              <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#0f3557] text-white text-xs font-bold">
                  2
                </span>
                Modify Properties
              </h4>

              {/* Font Family Dropdown */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Font Family
                </label>
                <div className="relative font-dropdown">
                  <button
                    onClick={() => setIsFontDropdownOpen(!isFontDropdownOpen)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 hover:border-gray-400 text-gray-700 font-medium rounded-md transition-colors flex items-center justify-between text-sm"
                  >
                    <span className="truncate">{selectedFont.name}</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className={`transition-transform ${isFontDropdownOpen ? 'rotate-180' : ''}`}
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  {isFontDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-auto">
                      {FONT_CATEGORIES.map((category) => {
                        const fontsInCategory = FONTS.filter(f => f.category === category);
                        if (fontsInCategory.length === 0) return null;
                        return (
                          <div key={category}>
                            <div className="px-3 py-1.5 bg-gray-100 text-xs font-bold text-gray-600 sticky top-0">
                              {category}
                            </div>
                            {fontsInCategory.map((font) => (
                              <button
                                key={font.name}
                                onClick={() => handleFontChange(font)}
                                className="w-full px-3 py-1.5 text-left text-sm hover:bg-blue-50 transition-colors"
                                style={{ fontFamily: font.family }}
                              >
                                {font.name}
                              </button>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Font Size & Text Style - Combined Row */}
              <div className="grid grid-cols-2 gap-3">
                {/* Font Size Controls */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Font Size
                  </label>
                  <div className="flex gap-1">
                    <button
                      onClick={handleDecreaseFonts}
                      className="w-10 h-10 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-md transition-colors flex items-center justify-center"
                      title="Decrease font size"
                    >
                      <span className="text-lg font-bold">A</span>
                      <span className="text-xs">⁻</span>
                    </button>
                    <div className="relative flex-1 size-dropdown">
                      <button
                        onClick={() => setIsSizeDropdownOpen(!isSizeDropdownOpen)}
                        className="w-full h-10 px-2 bg-white border border-gray-300 hover:border-gray-400 text-gray-700 font-medium rounded-md transition-colors flex items-center justify-between text-sm"
                      >
                        <span>{currentFontSize}</span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className={`transition-transform ${isSizeDropdownOpen ? 'rotate-180' : ''}`}
                        >
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>
                      {isSizeDropdownOpen && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-auto">
                          {FONT_SIZES.map((size) => (
                            <button
                              key={size}
                              onClick={() => handleFontSizeChange(size)}
                              className={`w-full px-3 py-1.5 text-left text-sm hover:bg-blue-50 transition-colors ${
                                size === currentFontSize ? 'bg-blue-100 font-semibold' : ''
                              }`}
                            >
                              {size}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={handleIncreaseFonts}
                      className="w-10 h-10 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-md transition-colors flex items-center justify-center"
                      title="Increase font size"
                    >
                      <span className="text-xl font-bold">A</span>
                      <span className="text-xs">⁺</span>
                    </button>
                  </div>
                </div>

                {/* Text Style Toggles */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Text Style
                  </label>
                  <div className="flex gap-1">
                    <button
                      onClick={handleToggleBold}
                      className={`flex-1 h-10 font-bold rounded-md transition-all ${
                        isBold
                          ? 'bg-[#0f3557] text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      title="Bold"
                    >
                      B
                    </button>
                    <button
                      onClick={handleToggleItalic}
                      className={`flex-1 h-10 italic font-semibold rounded-md transition-all ${
                        isItalic
                          ? 'bg-[#0f3557] text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      title="Italic"
                    >
                      I
                    </button>
                    <button
                      onClick={handleToggleUnderline}
                      className={`flex-1 h-10 underline font-semibold rounded-md transition-all ${
                        isUnderline
                          ? 'bg-[#0f3557] text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      title="Underline"
                    >
                      U
                    </button>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-gray-200" />

              {/* Style Presets */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-semibold text-gray-700">
                    Style Presets
                  </label>
                  <button
                    onClick={() => setShowSavePresetDialog(!showSavePresetDialog)}
                    className="px-2 py-1 bg-[#0f3557] text-white text-xs rounded-md hover:bg-[#0a2540] transition-colors flex items-center gap-1"
                    title="Save current settings as preset"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                      <polyline points="17 21 17 13 7 13 7 21" />
                      <polyline points="7 3 7 8 15 8" />
                    </svg>
                    Save
                  </button>
                </div>

                {/* Save Preset Dialog */}
                {showSavePresetDialog && (
                  <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Preset Name
                    </label>
                    <div className="flex gap-1.5">
                      <input
                        type="text"
                        value={newPresetName}
                        onChange={(e) => setNewPresetName(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && savePreset()}
                        placeholder="My Custom Style"
                        className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0f3557]"
                        autoFocus
                      />
                      <button
                        onClick={savePreset}
                        className="px-2 py-1 bg-green-600 text-white text-xs rounded-md hover:bg-green-700 transition-colors"
                      >
                        ✓
                      </button>
                      <button
                        onClick={() => {
                          setShowSavePresetDialog(false);
                          setNewPresetName('');
                        }}
                        className="px-2 py-1 bg-gray-400 text-white text-xs rounded-md hover:bg-gray-500 transition-colors"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}

                {/* Custom Presets */}
                {customPresets.length > 0 ? (
                  <div className="space-y-1">
                    {customPresets.map((preset) => (
                      <div
                        key={preset.name}
                        className="flex items-center gap-1.5 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-md p-1.5"
                      >
                        <button
                          onClick={() => loadPreset(preset)}
                          className="flex-1 text-left px-2 py-1 hover:bg-white/60 rounded transition-colors"
                        >
                          <div className="text-xs font-semibold text-gray-700">{preset.name}</div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {preset.font.name} • {preset.fontSize}px
                            {preset.isBold && ' • Bold'}
                            {preset.isItalic && ' • Italic'}
                            {preset.isUnderline && ' • Underline'}
                          </div>
                        </button>
                        <button
                          onClick={() => deletePreset(preset.name)}
                          className="px-1.5 py-1 text-red-600 hover:bg-red-100 rounded transition-colors"
                          title="Delete preset"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            <line x1="10" y1="11" x2="10" y2="17" />
                            <line x1="14" y1="11" x2="14" y2="17" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-xs text-gray-500">
                    No saved presets yet.
                    <br />
                    <span className="text-gray-400">Click "Save" to create your first preset.</span>
                  </div>
                )}
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-2.5">
                <div className="flex items-start gap-1.5">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 text-[#0f3557] flex-shrink-0 mt-0.5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="text-xs text-gray-700">
                    <p className="font-semibold mb-1">Usage:</p>
                    <ol className="list-decimal ml-3 space-y-0.5 text-xs">
                      <li>Select text element</li>
                      <li>Click text in drawing</li>
                      <li>Modify properties</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex-none bg-gray-50 px-4 py-2.5 border-t border-gray-200 flex justify-end items-center">
            <button
              onClick={handleClose}
              className="px-4 py-1.5 text-sm font-medium text-white bg-[#0f3557] hover:bg-[#0a2540] rounded-md transition-colors"
            >
              Close
            </button>
          </div>
        </motion.div>
      </div>
    </div>,
    document.body
  );
};

export default TextManipulationPanel;
